// app 1.4.58 (Alexander, 20 Oct) — the ladder of sources on the pay page and the embedded slider.
//
// A request names an amount in a currency the payer may not hold (Omega Air's fares are in the
// destination's money: € to Paris, £ to London, $ to New York). The Sending card has had rung 3
// since v0.90.19 — a shortfall in the pay currency is converted from another pocket inside the
// payment's own userOp (approve + MFx.swap + transfer, one Face ID). This is the same rung for a
// payment that arrives by link or through a merchant's page, in one place both views call.
//
// The order is the design's (decisions-log, "Swap is a means, not an intent"): the default pocket
// first, then the largest; only the shortfall is converted; the rung is simply absent when nothing
// can cover it — the slide then says so instead of failing on the chain.
import { encodeFunctionData, parseUnits } from "viem";
import type { WalletAdapter } from "./adapters/WalletAdapter";
import { MFxSource } from "./adapters/MFxSource";
import { currency, defaultCurrency, formatFx, liveCurrencies, type FxSource, type FxTopUp } from "./fx";
import { formatUsdShort } from "./money";

const ERC20_TRANSFER_ABI = [
  { type: "function", name: "transfer", stateMutability: "nonpayable", inputs: [{ name: "to", type: "address" }, { name: "amount", type: "uint256" }], outputs: [{ type: "bool" }] },
] as const;

export interface PayPlan {
  /** The pocket the shortfall comes from. */
  from: string;
  /** Units of the pay currency per unit of `from`, with M's spread. */
  rate: number;
  /** What the pay pocket lacks, in the pay currency. */
  shortfall: number;
  /** About what leaves `from` (the voucher prices it exactly). */
  sourceAmount: number;
  /** What the pay pocket holds. */
  pocket: number;
}

export type PayPlanResult =
  | { kind: "covered"; pocket: number }           // the pay pocket holds the amount
  | { kind: "plan"; plan: PayPlan }               // a rung covers the shortfall
  | { kind: "short"; pocket: number; need: number } // nothing covers it; `need` = what is missing, rounded up to the cent (1.5.33)
  | { kind: "unknown" };                          // no FX here, or the read failed: the chain decides, as before

/** The FX seam for a payer, when this build has MFx; null otherwise. */
export function fxFor(adapter: WalletAdapter, address: string): FxSource | null {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  if (!env?.VITE_FX_ADDRESS) return null;
  return new MFxSource(adapter, { apiBase: env.VITE_WORKER_URL ?? "", address });
}

const r2 = (n: number) => Math.round(n * 100) / 100;
/** What a pocket can pay, to the cent — FLOORED. A pocket that reads £0.40 may hold 0.399999 (six decimals on the token); converting
 *  exactly the rounded shortfall left £0.67 a few millionths short and the transfer reverted after Face ID (Alexander, 20 Oct). */
const floor2 = (n: number) => Math.floor(n * 100 + 1e-7) / 100;

/** What paying `amount` of `to` from `address` would take. */
export async function planPay(fx: FxSource | null, address: string, to: string, amount: number): Promise<PayPlanResult> {
  if (!fx?.capacity) return { kind: "unknown" };
  let pockets: Record<string, number>;
  try { pockets = await fx.getPockets(address); } catch { return { kind: "unknown" }; }
  const pocket = floor2(pockets[to] ?? 0);
  const shortfall = r2(amount - pocket);
  if (shortfall <= 0) return { kind: "covered", pocket };
  const def = defaultCurrency();
  // 1.5.33 — "You need $X more.": the most any one pocket could convert into the pay currency counts towards the order
  let best = 0;
  const shortBy = () => Math.max(0.01, Math.ceil((shortfall - best) * 100 - 1e-7) / 100);
  const cands = liveCurrencies()
    .filter((c) => c !== to && (pockets[c] ?? 0) > 0)
    .sort((a, b) => (a === def ? -1 : b === def ? 1 : 0) || (pockets[b] ?? 0) - (pockets[a] ?? 0));
  for (const from of cands) {
    try {
      const c = await fx.capacity(from, to);
      if (c.remaining === 0) { best = 0; return { kind: "short", pocket, need: shortBy() }; }
      let capOut = (pockets[from] ?? 0) * c.rate;
      if (c.maxIn != null) capOut = Math.min(capOut, c.maxIn * c.rate);
      if (c.bufferOut != null) capOut = Math.min(capOut, c.bufferOut);
      capOut = Math.floor(capOut * 100) / 100;
      best = Math.max(best, capOut);
      if (capOut + 1e-9 >= shortfall) return { kind: "plan", plan: { from, rate: c.rate, shortfall, sourceAmount: Math.ceil((shortfall / c.rate) * 100) / 100, pocket } };
    } catch {
      /* this pocket can't help right now — the next */
    }
  }
  return { kind: "short", pocket, need: shortBy() };
}

/** 1.5.33 (Alexander, 28 Oct) — the shortfall's line on the checkout and the pay page: "You need $0.35 more." */
export function needLine(to: string, need: number): string {
  return `You need ${fmt(to, need)} more.`;
}

const fmt = (code: string, n: number) => (code === "USD" ? formatUsdShort(n) : formatFx(code, n));

/** The card's line: "$0.63 converted", or "€0.10 + $0.52 converted" when the pay pocket has some of it. */
export function sourceLine(to: string, plan: PayPlan): string {
  const converted = `${fmt(plan.from, plan.sourceAmount)} converted`;
  return plan.pocket > 0 ? `${fmt(to, plan.pocket)} + ${converted}` : converted;
}

/** The sentence when nothing covers it. */
export function shortLine(to: string, amount: number, pocket: number): string {
  const name = currency(to).name.toLowerCase().replace(/^(us|british|south korean|south african|swiss|canadian|australian|mexican|brazilian|swedish) /, "");
  return pocket > 0 ? `You have ${fmt(to, pocket)} in ${name}, and nothing to convert the rest from.` : `Nothing in your pockets covers ${fmt(to, amount)}.`;
}

/** The voucher for the plan's shortfall, and the payment's calls: approve + swap + transfer, one userOp. */
export async function payWithTopUp(fx: FxSource, plan: PayPlan, to: string, toAddress: string, amount: string): Promise<{ topUp: FxTopUp; calls: { to: string; data: string }[] }> {
  if (!fx.topUp) throw new Error("no_topup");
  const topUp = await fx.topUp(plan.from, to, plan.shortfall);
  // Decision 3 (22 Sep): a voucher that prices the source side materially away from what the card showed is
  // not settled behind the person's back — materially: more than 1 % AND more than two cents (a 27-pence
  // shortfall rounds to the cent either way; 1 % alone looped "slide again" for ever on the phone, 20 Oct).
  // The plan takes the Worker's figure, as the Sending card does, so the next slide is settled at what it shows.
  const diff = Math.abs(topUp.amountIn - plan.sourceAmount);
  if (diff > 0.02 + 1e-9 && diff / plan.sourceAmount > 0.01) { plan.sourceAmount = topUp.amountIn; throw Object.assign(new Error("rate_moved"), { amountIn: topUp.amountIn }); }
  plan.sourceAmount = topUp.amountIn;
  const tok = currency(to).token!;
  return {
    topUp,
    calls: [
      ...topUp.calls,
      { to: tok.address, data: encodeFunctionData({ abi: ERC20_TRANSFER_ABI, functionName: "transfer", args: [toAddress as `0x${string}`, parseUnits(amount, tok.decimals)] }) },
    ],
  };
}
